import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Home, Users, CreditCard, TrendingUp, Activity, GripVertical, Zap, Building } from 'lucide-react';
import { 
  ClaraPageContent, 
  ClaraGrid, 
  ClaraCard, 
  ClaraKPICard,
  ClaraButton 
} from '../components/layout/ClaraLayoutShell';
import ClaraLayoutShell from '../components/layout/ClaraLayoutShell';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTheme } from '../contexts/ThemeContext';
import ClaraWelcomeHeader from "../components/molecules/ClaraWelcomeHeader";
import SessionMonitor from "../components/SessionMonitor";
import { propertiesService } from '../services/propertiesService';
import { masterSupabase } from '../lib/supabase-master.js';
import ActivityFeedDashboard from '../components/dashboard/ActivityFeedDashboard';
import FinancialAnalysisSlideshow from "../components/dashboard/FinancialAnalysisSlideshow";
import MaintenanceSummaryWidget from "../components/dashboard/MaintenanceSummaryWidget";
import TodoDashboardWidget from "../components/dashboard/TodoDashboardWidget";
import KPISlider from "../components/dashboard/KPISlider";
import { useTranslation } from 'react-i18next';

// Neue Widget-Imports
import ActionDashboardWidget from '../components/dashboard/ActionDashboardWidget';
import ShadowProactivityWidget from '../components/dashboard/ShadowProactivityWidget';
import AutomatedInsightsWidget from '../components/dashboard/AutomatedInsightsWidget';
import MarketIntelligenceWidget from '../components/dashboard/MarketIntelligenceWidget';
import CashflowForecastWidget from '../components/dashboard/CashflowForecastWidget';

// DND Kit Imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Widget Component
const SortableWidget = ({ id, children, isDragMode }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      {isDragMode && (
        <div
          {...attributes}
          {...listeners}
          className="absolute top-2 right-2 z-10 p-2 bg-white dark:bg-black rounded-lg shadow-lg cursor-grab active:cursor-grabbing hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
          aria-label="Widget verschieben"
        >
          <GripVertical className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        </div>
      )}
      {children}
    </div>
  );
};

/**
 * DashboardPageUnified - Live Dashboard with Real Database Data & Hierarchical Filtering
 * 
 * Features:
 * - Real-time data from Supabase
 * - Hierarchical filtering (Owner -> Property)
 * - Mobile-responsive design
 * - Interactive KPI cards with trend indicators
 * - Financial analysis slideshow
 * - Activity feed with real activities
 * - Maintenance summary with calendar integration
 * - Todo widget for task management
 * - Automated insights with AI-powered analysis
 * - Drag & Drop widget reordering with user-specific persistence
 * - FIXED: Correct data fields and enhanced KPIs
 */

// UUID validation helper function
const isValidUUID = (str) => {
  if (!str || str === 'all') return true;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

const DashboardPageUnified = () => {
  const location = useLocation();
  const [showOnboardingBanner, setShowOnboardingBanner] = React.useState(
    () => location.state?.onboardingCompleted === true
  );
  const { user } = useAuth(); // Get current user for filtering
  const { t } = useTranslation();
  
  // Existing state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    properties: 0,
    units: 0,
    tenants: 0,
    owners: 0,
    totalRent: 0,
    occupancyRate: 0,
    vacancyRate: 0,
    totalSqm: 0,
    utilitiesCost: 0,
    actualUtilitiesCost: 0,
    utilitiesBalance: 0,
    topResidentialTenant: null,
    topCommercialTenant: null,
    topAreaTenant: null,
    recentActivities: [],
    totalDeposits: 0,
    totalInvestment: 0
  });
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState("all");
  const [selectedProperty, setSelectedProperty] = useState("all");

  // NEW: Drag & Drop states
  const [isDragMode, setIsDragMode] = useState(false);
  const [widgetOrder, setWidgetOrder] = useState([
    'action-dashboard',
    'shadow-proactivity',
    'kpi-slider',
    'financial-analysis',
    'cashflow-forecast',
    'market-intelligence',
    'automated-insights',
    'maintenance-summary',
    'activity-feed'
  ]);

  // DND Kit Sensors - Optimized for mobile
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load user's widget order from Supabase
  const loadWidgetOrder = useCallback(async () => {
    try {
      const { data: { user } } = await masterSupabase.auth.getUser();
      if (!user) return;

      const { data, error } = await masterSupabase
        .from('user_dashboard_layouts')
        .select('widget_order')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.warn('Widget order not available (table may not exist):', error);
        return;
      }

      if (data?.widget_order) {
        const savedOrder = Array.isArray(data.widget_order) ? [...data.widget_order] : [];
        if (!savedOrder.includes('shadow-proactivity')) {
          const actionIndex = savedOrder.indexOf('action-dashboard');
          savedOrder.splice(actionIndex >= 0 ? actionIndex + 1 : 0, 0, 'shadow-proactivity');
        }
        setWidgetOrder(savedOrder);
      }
    } catch (error) {
      console.warn('Widget order not available (table may not exist):', error);
    }
  }, [user?.id]);

  // Save user's widget order to Supabase
  const saveWidgetOrder = useCallback(async (newOrder) => {
    try {
      const { data: { user } } = await masterSupabase.auth.getUser();
      if (!user) return;

      const { error } = await masterSupabase
        .from('user_dashboard_layouts')
        .upsert({
          user_id: user.id,
          widget_order: newOrder
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.warn('Cannot save widget order (table may not exist):', error);
      }
    } catch (error) {
      console.warn('Cannot save widget order (table may not exist):', error);
    }
  }, [user?.id]);

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setWidgetOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        
        // Save to Supabase
        saveWidgetOrder(newOrder);
        
        return newOrder;
      });
    }
  };

  // Toggle drag mode
  const toggleDragMode = () => {
    setIsDragMode(!isDragMode);
  };

  // FIXED: Enhanced loadDashboardStats with correct data fields
  const loadDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔍 DEBUG: Starting loadDashboardStats with filters:', { selectedOwner, selectedProperty });
      
      // Step 1: Load filtered properties
      let propertiesQuery = masterSupabase.from('properties').select('*');
      
      // CRITICAL: Get current authenticated user for secure database queries
      const { data: { user: currentUser } } = await masterSupabase.auth.getUser();
      if (!currentUser?.id) {
        console.log("🚫 No user ID available, cannot load dashboard stats");
        setDashboardData({ owners: 0, properties: 0, units: 0, tenants: 0, totalRent: 0 });
        return;
      }
      console.log("🔍 DEBUG: Using authenticated user ID:", currentUser.id);
      propertiesQuery = propertiesQuery.eq("created_by", currentUser.id);
      if (selectedOwner && selectedOwner !== 'all' && isValidUUID(selectedOwner)) {
        propertiesQuery = propertiesQuery.eq('owner_entity', selectedOwner);
      }
      if (selectedProperty && selectedProperty !== 'all' && isValidUUID(selectedProperty)) {
        propertiesQuery = propertiesQuery.eq('id', selectedProperty);
      }
      
      const { data: filteredProperties, error: propertiesError } = await propertiesQuery;
      if (propertiesError) throw propertiesError;

      // Step 2: Load units for these properties
      const propertyIds = filteredProperties?.map(p => p.id) || [];
      let units = [];
      if (propertyIds.length > 0) {
        const { data: unitsData, error: unitsError } = await masterSupabase
          .from('units')
          .select('*')
          .in('property_id', propertyIds);
        if (unitsError) throw unitsError;
        units = unitsData || [];
      }
      
      // Step 3: Load tenants for these units
      const unitIds = units.map(u => u.id);
      let tenants = [];
      if (unitIds.length > 0) {
        const { data: tenantsData, error: tenantsError } = await masterSupabase
          .from('tenants')
          .select('*')
          .in('unit_id', unitIds);
        if (tenantsError) throw tenantsError;
        tenants = tenantsData || [];
      }

      // Step 4: Load operating costs for utilities calculation
      let operatingCosts = [];
      if (propertyIds.length > 0) {
        const { data: costsData, error: costsError } = await masterSupabase
          .from('operating_costs')
          .select('*')
          .in('property_id', propertyIds)
          .gte('date', new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString());
        if (!costsError) {
          operatingCosts = costsData || [];
        }
      }

      // Step 5: Calculate statistics with CORRECT data fields
      const totalRent = tenants.reduce((sum, tenant) => {
        return sum + (parseFloat(tenant.cold_rent) || 0); // FIXED: cold_rent instead of rent_amount
      }, 0);

      const totalDeposits = tenants.reduce((sum, tenant) => {
        return sum + (parseFloat(tenant.deposit) || 0);
      }, 0);

      const utilitiesCost = tenants.reduce((sum, tenant) => {
        return sum + (parseFloat(tenant.utilities_cost) || 0);
      }, 0);

      const actualUtilitiesCost = operatingCosts.reduce((sum, cost) => {
        return sum + (parseFloat(cost.amount) || 0);
      }, 0);

      const utilitiesBalance = utilitiesCost - actualUtilitiesCost;

      const occupiedUnits = tenants.length;
      const totalUnits = units.length;
      const occupancyRate = totalUnits > 0 ? (occupiedUnits / totalUnits) * 100 : 0;
      const vacancyRate = totalUnits > 0 ? ((totalUnits - occupiedUnits) / totalUnits) * 100 : 0;

      const totalSqm = units.reduce((sum, unit) => {
        return sum + (parseFloat(unit.size_sqm) || 0);
      }, 0);

      // Get unique owners count
      const totalInvestment = filteredProperties.reduce((sum, property) => {
        return sum + (parseFloat(property.purchase_price) || 0);
      }, 0);

      const uniqueOwners = new Set(filteredProperties?.map(p => p.owner_entity));

      // Calculate Top-Tenants for KPI tiles
      const enrichTenantWithUnitSize = (tenant) => {
        const unit = units.find(u => u.id === tenant.unit_id);
        return {
          ...tenant,
          unit_size: unit ? unit.size_sqm : 0
        };
      };

      const residentialTenants = tenants.filter(t => t.tenant_type === 'private');
      const commercialTenants = tenants.filter(t => t.tenant_type === 'commercial');

      const topResidentialTenant = residentialTenants.length > 0 ? 
        residentialTenants
          .map(enrichTenantWithUnitSize)
          .filter(t => t.unit_size > 0)
          .reduce((top, tenant) => {
            const currentRentPerSqm = (tenant.cold_rent || 0) / tenant.unit_size;
            const topRentPerSqm = top ? (top.cold_rent || 0) / top.unit_size : 0;
            return currentRentPerSqm > topRentPerSqm ? tenant : top;
          }, null) : null;

      const topCommercialTenant = commercialTenants.length > 0 ? 
        commercialTenants
          .map(enrichTenantWithUnitSize)
          .filter(t => t.unit_size > 0)
          .reduce((top, tenant) => {
            const currentRentPerSqm = (tenant.cold_rent || 0) / tenant.unit_size;
            const topRentPerSqm = top ? (top.cold_rent || 0) / top.unit_size : 0;
            return currentRentPerSqm > topRentPerSqm ? tenant : top;
          }, null) : null;

      const topAreaTenant = tenants.length > 0 ? 
        tenants
          .map(enrichTenantWithUnitSize)
          .filter(t => t.unit_size > 0)
          .reduce((top, tenant) => {
            return tenant.unit_size > (top?.unit_size || 0) ? tenant : top;
          }, null) : null;

      // Step 6: Load financing data for charts
      console.log('🔍 DEBUG: Loading financing data...');
      // Use the same currentUser from above
      let financingCosts = {
        monthly: 0,
        annual: 0,
        totalLoanAmount: 0,
        averageInterestRate: 0,
        byProperty: []
      };

      if (currentUser?.id) {
        const { data: financingsData, error: financingsError } = await masterSupabase
          .from("financings")
          .select("*")
          .eq("user_id", currentUser.id);

        if (!financingsError && financingsData?.length > 0) {
          // Calculate financing costs
          const totalLoanAmount = financingsData.reduce((sum, f) => sum + (parseFloat(f.loan_amount) || 0), 0);
          const totalInterestRate = financingsData.reduce((sum, f) => sum + (parseFloat(f.interest_rate_apy) || 0), 0);
          const averageInterestRate = financingsData.length > 0 ? totalInterestRate / financingsData.length : 0;
          
          // Calculate monthly costs (simplified: loan_amount * interest_rate / 12)
          const monthlyFinancingCosts = financingsData.reduce((sum, f) => {
            const loanAmount = parseFloat(f.loan_amount) || 0;
            const interestRate = parseFloat(f.interest_rate_apy) || 0;
            return sum + (loanAmount * (interestRate / 100) / 12);
          }, 0);

          financingCosts = {
            monthly: monthlyFinancingCosts,
            annual: monthlyFinancingCosts * 12,
            totalLoanAmount,
            averageInterestRate,
            byProperty: financingsData
          };
          
          console.log('🔍 DEBUG: Calculated financing costs:', financingCosts);
        }
      }

      console.log('🔍 DEBUG: Setting dashboard data:', { 
        owners: uniqueOwners.size, 
        properties: filteredProperties?.length || 0, 
        units: units.length, 
        tenants: tenants.length, 
        totalRent, 
        occupancyRate, 
        vacancyRate,
        totalSqm,
        selectedOwner,
        selectedProperty
      });
      
      // DETAILED DEBUG for data inconsistencies
      console.log('🔍 DETAILED DEBUG:');
      console.log('- Filtered Properties:', filteredProperties?.map(p => ({ id: p.id, name: p.name, owner_entity: p.owner_entity })));
      console.log('- Units with areas:', units.map(u => ({ id: u.id, property_id: u.property_id, size_sqm: u.size_sqm, unit_number: u.unit_number, name: u.name })));
console.log('- Units with NULL areas:', units.filter(u => !u.area || u.area === null || u.area === 0));
      console.log('- Financing costs:', financingCosts);
      console.log('- Annual revenue for financing ratio:', totalRent * 12);
      console.log('- Financing ratio calculation:', financingCosts.annual, '/', (totalRent * 12), '=', ((financingCosts.annual || 0) / ((totalRent * 12) || 1)) * 100);
      
      setDashboardData({
        owners: uniqueOwners.size,
        properties: filteredProperties?.length || 0,
        units: units.length,
        tenants: tenants.length,
        totalRent,
        occupancyRate,
        vacancyRate,
        totalSqm,
        utilitiesCost,
        actualUtilitiesCost,
        utilitiesBalance,
        topResidentialTenant,
        topCommercialTenant,
        topAreaTenant,
        totalDeposits,
        totalInvestment,
        financingCosts, // Add financing data to dashboard data
        roi: totalInvestment > 0 ? ((totalRent * 12) / totalInvestment) * 100 : 0,
        roiCommercial: (() => {
          const commercialProperties = filteredProperties.filter(p => 
            p.property_type?.toLowerCase().includes('gewerbe') || 
            p.property_type?.toLowerCase().includes('commercial')
          );
          const commercialInvestment = commercialProperties.reduce((sum, p) => sum + (parseFloat(p.purchase_price) || 0), 0);
          const commercialUnits = units.filter(u => 
            commercialProperties.some(p => p.id === u.property_id)
          );
          const commercialTenants = tenants.filter(t => 
            commercialUnits.some(u => u.id === t.unit_id)
          );
          const commercialRent = commercialTenants.reduce((sum, t) => sum + (parseFloat(t.cold_rent) || 0), 0);
          return commercialInvestment > 0 ? ((commercialRent * 12) / commercialInvestment) * 100 : 0;
        })(),
        roiResidential: (() => {
          const residentialProperties = filteredProperties.filter(p => 
            p.property_type?.toLowerCase().includes('mehrfamilien') || 
            p.property_type?.toLowerCase().includes('wohn') ||
            p.property_type?.toLowerCase().includes('residential')
          );
          const residentialInvestment = residentialProperties.reduce((sum, p) => sum + (parseFloat(p.purchase_price) || 0), 0);
          const residentialUnits = units.filter(u => 
            residentialProperties.some(p => p.id === u.property_id)
          );
          const residentialTenants = tenants.filter(t => 
            residentialUnits.some(u => u.id === t.unit_id)
          );
          const residentialRent = residentialTenants.reduce((sum, t) => sum + (parseFloat(t.cold_rent) || 0), 0);
          return residentialInvestment > 0 ? ((residentialRent * 12) / residentialInvestment) * 100 : 0;
        })()
      });
      
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOwner, selectedProperty]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No token found');
        return;
      }

      // Load regular dashboard data
      const response = await fetch('/api/dashboard-data', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Load intelligence data
        const intelligenceResponse = await fetch('/api/financial-insights', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (intelligenceResponse.ok) {
          const intelligence = await intelligenceResponse.json();
          data.intelligence = intelligence;
        }
        
        setDashboardData(data);
      } else {
        console.error('Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFilters = async () => {
    try {
      if (!user?.id) {
        console.log("🚫 No user ID available, cannot load owners");
        setOwners([]);
        setProperties([]);
        return;
      }

      console.log("🔍 Loading owners and properties for user:", user.id);
      
      const [ownersResult, propertiesResult] = await Promise.all([
        masterSupabase.from("owners").select("*").eq("created_by", user.id).order("name"),
        masterSupabase.from("properties").select("*").eq("created_by", user.id).order("property_name")
      ]);
      
      if (ownersResult.error) throw ownersResult.error;
      if (propertiesResult.error) throw propertiesResult.error;
      
      console.log(`✅ Loaded ${ownersResult.data?.length || 0} owners and ${propertiesResult.data?.length || 0} properties for user ${user.id}`);
      
      setOwners(ownersResult.data || []);
      setProperties(propertiesResult.data || []);
      
    } catch (error) {
      console.error('Error loading filters:', error);
    }
  };

  // Load data on mount
  useEffect(() => {
    loadDashboardData();
    if (user?.id) { loadFilters(); }
    loadWidgetOrder();
  }, [loadWidgetOrder]);

  useEffect(() => {
    if (properties.length > 0) {
      loadDashboardStats();
    }
  }, [selectedOwner, selectedProperty, properties.length, user?.id, loadDashboardStats]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Render widgets based on order
  const renderWidget = (widgetId) => {
    switch (widgetId) {
      case 'action-dashboard':
        return (
          <SortableWidget key={widgetId} id={widgetId} isDragMode={isDragMode}>
            <div className="mt-6 sm:mt-8">
              <ActionDashboardWidget 
                selectedOwner={selectedOwner}
                selectedProperty={selectedProperty}
              />
            </div>
          </SortableWidget>
        );
        
      case 'shadow-proactivity':
        return (
          <SortableWidget key={widgetId} id={widgetId} isDragMode={isDragMode}>
            <div className="mt-6 sm:mt-8">
              <ShadowProactivityWidget />
            </div>
          </SortableWidget>
        );

      case 'kpi-slider':
        return (
          <SortableWidget key={widgetId} id={widgetId} isDragMode={isDragMode}>
            <KPISlider 
              dashboardData={dashboardData}
              loading={loading}
              selectedOwner={selectedOwner}
              selectedProperty={selectedProperty}
            />
          </SortableWidget>
        );
      
      case 'financial-analysis':
        return (
          <SortableWidget key={widgetId} id={widgetId} isDragMode={isDragMode}>
            <FinancialAnalysisSlideshow 
              selectedOwner={selectedOwner} 
              selectedProperty={selectedProperty}
              dashboardData={dashboardData}
            />
          </SortableWidget>
        );
      
      case 'cashflow-forecast':
        return (
          <SortableWidget key={widgetId} id={widgetId} isDragMode={isDragMode}>
            <div className="mt-6 sm:mt-8">
              <CashflowForecastWidget 
                selectedOwner={selectedOwner}
                selectedProperty={selectedProperty}
              />
            </div>
          </SortableWidget>
        );
      
      case 'market-intelligence':
        return (
          <SortableWidget key={widgetId} id={widgetId} isDragMode={isDragMode}>
            <div className="mt-6 sm:mt-8">
              <MarketIntelligenceWidget 
                selectedOwner={selectedOwner}
                selectedProperty={selectedProperty}
              />
            </div>
          </SortableWidget>
        );
      
      case 'automated-insights':
        return (
          <SortableWidget key={widgetId} id={widgetId} isDragMode={isDragMode}>
            <div className="mt-6 sm:mt-8">
              <AutomatedInsightsWidget 
                selectedOwner={selectedOwner}
                selectedProperty={selectedProperty}
              />
            </div>
          </SortableWidget>
        );
      
      case 'maintenance-summary':
        return (
          <SortableWidget key={widgetId} id={widgetId} isDragMode={isDragMode}>
            <div className="mt-6 sm:mt-8">
              <MaintenanceSummaryWidget 
                selectedOwner={selectedOwner}
                selectedProperty={selectedProperty}
              />
            </div>
          </SortableWidget>
        );
      
      case 'activity-feed':
        return (
          <SortableWidget key={widgetId} id={widgetId} isDragMode={isDragMode}>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6 mt-6 sm:mt-8">
              <ClaraCard>
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Letzte Aktivitäten
                  </h3>
                  <ActivityFeedDashboard 
                    compact={true}
                    maxItems={5}
                    showFilters={false}
                    showHeader={false}
                    selectedOwner={selectedOwner}
                    selectedProperty={selectedProperty}
                  />
                </div>
              </ClaraCard>
              <ClaraCard>
                <div className="p-4 sm:p-6">
                  <TodoDashboardWidget />
                </div>
              </ClaraCard>
            </div>
          </SortableWidget>
        );
      
      default:
        return null;
    }
  };

  return (
    <ClaraLayoutShell suppressHydrationWarning>
      {showOnboardingBanner && (
        <div style={{
          position: 'fixed', top: '1rem', left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999, background: 'linear-gradient(to right, #06b6d4, #8b5cf6)',
          color: 'white', padding: '0.75rem 1.5rem', borderRadius: '0.75rem',
          boxShadow: '0 20px 40px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: '0.75rem'
        }}>
          <span style={{fontSize: '1.25rem'}}>🎉</span>
          <span style={{fontWeight: 600}}>Onboarding abgeschlossen — Clara ist bereit.</span>
          <button onClick={() => setShowOnboardingBanner(false)}
            style={{marginLeft: '1rem', background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', fontSize: '1.25rem', cursor: 'pointer', fontWeight: 'bold'}}>×</button>
        </div>
      )}
      <ClaraPageContent>
        {/* Clara Welcome Header with Hierarchical Filters */}
        <ClaraWelcomeHeader 
          selectedOwner={selectedOwner}
          setSelectedOwner={setSelectedOwner}
          owners={owners}
          selectedProperty={selectedProperty}
          setSelectedProperty={setSelectedProperty}
          properties={properties}
          setProperties={setProperties}
        />

        {/* Intelligence Alert Badges */}
        {dashboardData?.intelligence?.expiringContracts?.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {dashboardData.intelligence.expiringContracts.filter(c => c.days_until_expiry <= 0).length > 0 && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 animate-pulse">
                🚨 {dashboardData.intelligence.expiringContracts.filter(c => c.days_until_expiry <= 0).length} Verträge abgelaufen
              </div>
            )}
            {dashboardData.intelligence.expiringContracts.filter(c => c.days_until_expiry > 0 && c.days_until_expiry <= 30).length > 0 && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                ⚠️ {dashboardData.intelligence.expiringContracts.filter(c => c.days_until_expiry > 0 && c.days_until_expiry <= 30).length} Verträge kritisch
              </div>
            )}
            {dashboardData.intelligence.expiringContracts.filter(c => c.days_until_expiry > 30 && c.days_until_expiry <= 90).length > 0 && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                ℹ️ {dashboardData.intelligence.expiringContracts.filter(c => c.days_until_expiry > 30 && c.days_until_expiry <= 90).length} Verträge bevorstehend
              </div>
            )}
          </div>
        )}

        {/* Session Monitor */}
        {/* <SessionMonitor /> */}

        {/* Sortable Widgets Container - Button moved to floating position */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={widgetOrder} strategy={verticalListSortingStrategy}>
            <div className="space-y-6">
              {widgetOrder.map(renderWidget)}
            </div>
          </SortableContext>
        </DndContext>

        {/* Drag Mode Instructions */}
        {isDragMode && (
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Anleitung:</strong> Verwenden Sie die ⋮⋮ Griffe, um Widgets zu verschieben. 
              Ihre Anordnung wird automatisch gespeichert.
            </p>
          </div>
        )}
      </ClaraPageContent>

      {/* Floating Dashboard Anpassen Button - Bottom Right */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={toggleDragMode}
          className={`px-4 py-2 rounded-full font-medium transition-all duration-300 shadow-lg hover:shadow-xl ${
            isDragMode 
              ? 'bg-blue-600 text-white hover:bg-blue-700' 
              : 'bg-white dark:bg-black text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 border border-gray-300 dark:border-gray-600'
          }`}
          title={isDragMode ? 'Bearbeitung beenden' : 'Dashboard anpassen'}
        >
          {isDragMode ? 'Bearbeitung beenden' : 'Dashboard anpassen'}
        </button>
      </div>
    </ClaraLayoutShell>
  );
};


      {/* DEBUG INFO - VISIBLE IN UI */}
      <div className="fixed bottom-2 left-2 bg-red-500 bg-opacity-70 text-white text-xs px-2 py-1 rounded z-50">
        DashboardPageUnified v20250903_111353
      </div>
export default DashboardPageUnified;

// Cache bust Dashboard Fix Wed Sep  3 11:11:08 UTC 2025
